import { useState, useEffect, useMemo, useCallback } from "react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import toast, { Toaster } from "react-hot-toast";
import SingleDatePicker from "./SingleDatePicker";
import WorkOrderTable from "./WorkOrderTable";
import UnassignedWOTable from "./UnassignedWOTable";
import RecurringTasks from "./RecurringTasks";
import { getAllergenRiskLevel, AllergenLegend } from "../utils/utils";
import { rooms } from "../constants/rooms";
import {
  getUnassignedWorkOrders,
  assignRoom,
  getAllWorkOrdersByRoom,
  updateAssignedDate,
  getKPIWorkOrders,
  getCategoriesList,
  unassignWorkOrder,
  getPostCleaningConfig,
  refreshWorkOrders,
  reorderWorkOrders,
  getCleaningTaskConfig,
  updateCleaningTaskConfig,
  addCleaningTaskToSchedule,
  renameCleaningTaskInSchedule,
  removeCleaningTaskFromSchedule,
  getRoomAllergenPolicies,
  updateRoomAllergenPolicies,
  lockScheduleKpiBaseline,
} from "../api/workOrder";
import "jspdf-autotable";
import "./workorderCustomStyle.css";
import AssignErrorModal from "../modals/AssignErrorModal";
import { updateProduct, syncErpagProducts } from "../api/ProductsApis";
import normalizeAllergen from "../utils/normalizeAllergen";
import { AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const ALLERGEN_OPTIONS = ["Mustard", "Sesame", "Celery", "Dairy"];
const NONE_ALLERGEN_VALUES = new Set([
  "",
  "0",
  "n/a",
  "none",
  "#n/a",
  "na",
  "n.a.",
]);
const ALLERGEN_CANONICAL_MAP = new Map(
  ALLERGEN_OPTIONS.map((allergen) => [allergen.toLowerCase(), allergen]),
);

const toCanonicalAllowedAllergen = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return ALLERGEN_CANONICAL_MAP.get(normalized) || null;
};

const normalizeIncomingAllergenPart = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || NONE_ALLERGEN_VALUES.has(trimmed.toLowerCase())) return null;
  return toCanonicalAllowedAllergen(trimmed) || trimmed;
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultRoomAllergenPolicies = () =>
  rooms.map((room) => {
    if (room === "Room 1" || room === "Room 2") {
      return { room, allowedAllergens: [] };
    }

    return { room, allowedAllergens: [...ALLERGEN_OPTIONS] };
  });

const normalizeRoomAllergenPolicies = (policies = []) => {
  const defaultPolicies = buildDefaultRoomAllergenPolicies();
  const policyMap = new Map(
    defaultPolicies.map((policy) => [policy.room, policy.allowedAllergens]),
  );

  if (Array.isArray(policies)) {
    policies.forEach((policy) => {
      if (!policy || typeof policy !== "object") return;
      const room = typeof policy.room === "string" ? policy.room.trim() : "";
      if (!room || !policyMap.has(room)) return;

      const allowedAllergens = Array.isArray(policy.allowedAllergens)
        ? [
            ...new Set(
              policy.allowedAllergens
                .map((allergen) => toCanonicalAllowedAllergen(allergen))
                .filter(Boolean),
            ),
          ]
        : [];

      policyMap.set(room, allowedAllergens);
    });
  }

  return rooms.map((room) => ({
    room,
    allowedAllergens: policyMap.get(room) || [],
  }));
};

const parseAllergenValues = (value) => {
  const normalized = normalizeAllergen(value);
  if (normalized === "None") return [];

  return normalized
    .split(",")
    .map((part) => normalizeIncomingAllergenPart(part))
    .filter(Boolean);
};

const resolveWorkOrderLabel = (item = {}) => {
  const candidates = [
    item.label,
    item.labelCategory,
    item.labelType,
    item.label_type,
    item.product?.label,
    item.product?.labelCategory,
    item.product?.labelType,
    item.product?.customFields?.labelCategory,
    item.product?.customFields?.labelType,
    item.customFields?.labelCategory,
    item.customFields?.labelType,
  ];

  for (const value of candidates) {
    const normalized = String(value || "").trim();
    if (normalized) return normalized;
  }
  return "";
};

export default function WorkOrderUI() {
  const [roomWorkOrders, setRoomWorkOrders] = useState([]);
  const [isRoomLoading, setIsRoomLoading] = useState(false);
  const [isSyncingProducts, setIsSyncingProducts] = useState(false);

  const handleSyncProducts = async () => {
    try {
      setIsSyncingProducts(true);
      const res = await syncErpagProducts();
      toast.success(res?.message || "Product refreshed successfully!", { duration: 4000, position: "top-right" });
    } catch (err) {
      toast.error("Failed to start product sync.", { duration: 4000, position: "top-right" });
    } finally {
      setIsSyncingProducts(false);
    }
  };

  const [roomError, setRoomError] = useState(null);
  const [apiUnassignedWorkOrders, setApiUnassignedWorkOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRoom, setSelectedRoom] = useState("Room 1");
  const [kpiRoom, setKpiRoom] = useState("ALL");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workOrdersByDate, setWorkOrdersByDate] = useState({});
  const [unassignedWOs, setUnassignedWOs] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState({});
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLockingScheduleBaseline, setIsLockingScheduleBaseline] =
    useState(false);
  const [isRecurringTaskModalOpen, setIsRecurringTaskModalOpen] =
    useState(false);
  const [isRoomAllergenModalOpen, setIsRoomAllergenModalOpen] = useState(false);
  const [roomAllergenPolicies, setRoomAllergenPolicies] = useState(
    buildDefaultRoomAllergenPolicies(),
  );
  const [roomAllergenDraft, setRoomAllergenDraft] = useState(
    buildDefaultRoomAllergenPolicies(),
  );
  const [isSavingRoomAllergenPolicies, setIsSavingRoomAllergenPolicies] =
    useState(false);
  const [kpiData, setKpiData] = useState({
    completionRate: 0,
    plannedVsActual: { planned: 0, actual: 0 },
    roomUtilization: 0,
  });
  const [scheduleKpiData, setScheduleKpiData] = useState({
    currentScheduledPlannedQty: 0,
    originalKpiBaselineQty: null,
    actualCompletedQty: 0,
    performanceRate: 0,
    baselineLocked: false,
  });

  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState(null);

  const [assignError, setAssignError] = useState(null);
  const [showAssignErrorModal, setShowAssignErrorModal] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [postCleaningConfigs, setPostCleaningConfigs] = useState([]);
  const [cleaningTaskConfig, setCleaningTaskConfig] = useState({
    weeklyMinutes: 120,
    monthlyMinutes: 180,
    trimesterMinutes: 300,
    customMinutes: 30,
  });
  const [cleaningTaskError, setCleaningTaskError] = useState(null);
  const [customTaskDuration, setCustomTaskDuration] = useState(30);
  const [isEditingCleaningConfig, setIsEditingCleaningConfig] = useState(false);
  const [isSavingCleaningConfig, setIsSavingCleaningConfig] = useState(false);
  const [cleaningConfigDraft, setCleaningConfigDraft] = useState({
    weeklyMinutes: 120,
    monthlyMinutes: 180,
    trimesterMinutes: 300,
  });
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [overtimeDetails, setOvertimeDetails] = useState(null);
  const { canWrite } = useAuth();
  const canAssignWorkOrders = canWrite("ERPagWorkOrder");
  const canTransferCompleted = canWrite("ERPagWorkOrder");
  const canCreateRecurringTasks = canWrite("ERPagWorkOrder");
  const canPlaceOnHold = canWrite("ERPagWorkOrder");
  const canUseContextMenu = canWrite("ERPagWorkOrder");
  const canManageRoomAllergenPolicies = canWrite("ERPagWorkOrder");

  const isAllergenAllowedForRoom = (roomName, allergenValue) => {
    const allergenParts = parseAllergenValues(allergenValue);
    if (allergenParts.length === 0) {
      return { allowed: true };
    }

    const policy = roomAllergenPolicies.find((item) => item.room === roomName);
    const allowedSet = new Set(
      (policy?.allowedAllergens || [])
        .map((item) => String(item || "").trim().toLowerCase())
        .filter(Boolean),
    );
    const disallowed = allergenParts.filter(
      (item) => !allowedSet.has(String(item || "").trim().toLowerCase()),
    );

    if (disallowed.length === 0) {
      return { allowed: true };
    }

    return {
      allowed: false,
      disallowed,
      allowedAllergens: policy?.allowedAllergens || [],
    };
  };

  const validateRoomAllergenRule = (workOrder, targetRoom, targetDateLabel) => {
    const validation = isAllergenAllowedForRoom(
      targetRoom,
      workOrder?.allergen || workOrder?.allergens,
    );

    if (validation.allowed) return true;

    const disallowedText = validation.disallowed?.join(", ");
    const allowedText =
      validation.allowedAllergens && validation.allowedAllergens.length > 0
        ? validation.allowedAllergens.join(", ")
        : "None";

    toast.error(
      `${targetRoom} does not allow ${disallowedText} allergens on ${targetDateLabel}. Allowed: ${allowedText}.`,
      { duration: 5000, position: "top-right" },
    );
    return false;
  };

  const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const refreshGlobalKPIs = async () => {
    await fetchKPIWorkOrders("ALL", selectedDate, selectedDate);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isReadOnly = false;
  // selectedDate.getTime() < today.getTime() && !isSameDay(selectedDate, today);

  const fetchUnassignedWorkOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getUnassignedWorkOrders();
      setApiUnassignedWorkOrders(response.data.data);
    } catch (err) {
      setError("Failed to fetch unassigned work orders.");
      if (err?.response?.status !== 401) {
        toast.error("Failed to fetch unassigned work orders.", {
          duration: 4000,
          position: "top-right",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const transformTimelineItems = (items, dateKey) => {
    const normalizeType = (typeValue = "") =>
      String(typeValue).trim().toLowerCase();
    const isWorkOrderItem = (item) => {
      const normalizedType = normalizeType(item?.type);
      return (
        (item?._id && item?.woNumber) ||
        normalizedType === "workorder" ||
        normalizedType === "work order"
      );
    };

    const timelineItems = (items || [])
      .filter((item) => item.startTime)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    return timelineItems.map((item, index) => {
      if (isWorkOrderItem(item)) {
        let scheduledTime = "00:00";

        if (item.scheduledHour) {
          const regex =
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)$/i;
          const match = item.scheduledHour.match(regex);
          if (match) {
            let [, month, day, year, hours, minutes] = match;
            hours = parseInt(hours, 10);
            if (match[7].toUpperCase() === "PM" && hours !== 12) hours += 12;
            if (match[7].toUpperCase() === "AM" && hours === 12) hours = 0;
            scheduledTime = `${hours.toString().padStart(2, "0")}:${minutes}`;
          }
        }

        const allergen = normalizeAllergen(item.allergens);
        const label = resolveWorkOrderLabel(item);

        return {
          _id: item._id,
          orderId: item.orderId,
          woNumber: item.woNumber || item.orderId || `WO-${index + 1}`,
          projectNumber: String(item.projectNumber ?? "-"),
          sku: item.sku || null,
          title: item.title,
          label,
          labelCategory: label,
          allergen,
          machineTool: item.product?.machine_tool || item.machine_tool || "",
          netWeight:
            item.netWeight && item.netWeight !== "0.0000000000"
              ? `${parseFloat(item.netWeight).toFixed(2)} ${
                  item.netWeightCustom || ""
                }`
              : "",
          plannedQty: Number(item.plannedQty) || 0,
          actualQty: Number(item.actualQty) || 0,
        
          currentErpagQty: Number(item.currentErpagQty ?? item.plannedQty) || 0,
          kpiPlannedQty:
            item.kpiPlannedQty === null || item.kpiPlannedQty === undefined
              ? null
              : Number(item.kpiPlannedQty) || 0,
          kpiQtyLocked: Boolean(item.kpiQtyLocked),
          status:
            String(item.status || "").toLowerCase() === "on hold"
              ? "LAB_HOLD"
              : String(item.status || "").toLowerCase(),
          room: item.room,
          date: dateKey,
          scheduledTime,
          warehouse: item.warehouse || "Spice Factory",
          duration: item.totalDurationMinutes || 60,
          lotNumber: item.lotNumber || null,
          assignedDate: item.assignedDate
            ? new Date(item.assignedDate).toISOString().split("T")[0]
            : null,
          isArchived: false,
          slot: item.slot || null,
          totalDurationMinutes: item.totalDurationMinutes || 60,
          type: "workorder",
          startTime: item.startTime,
          endTime: item.endTime,
          breaksIncluded: item?.breaksIncluded,
          breakTimeAdded: item?.breakTimeAdded,
           breakSummary: item?.breakSummary,
           onlineQty: item.onlineQty || 0,
           packing:
             item.packing ??
             item.packaging ??
             item.product?.packing ??
             item.product?.packaging ??
             "",
           isTransferred: item.isTransferred || false,
           markedAsCompletedLocally: item.markedAsCompletedLocally || false,
         };
       }

      const start = new Date(item.startTime);
      const end = new Date(item.endTime);
      const duration = Math.round((end - start) / 60000);

      let title = item.title;
      if (item.type === "lunch break") title = "Lunch Break";
      if (item.type === "morning break") title = "Morning Break";
      if (item.type === "afternoon break") title = "Afternoon Break";

      return {
        _id: item._id || `event-${item.type}-${index}`,
        type: item.type,
        title,
        duration,
        slot: item.slot,
        startTime: item.startTime,
        endTime: item.endTime,
        isTimelineEvent: true,
        isManualCleaningTask: item.type === "manual cleaning",
        cleaningTaskType:
          item.cleaningTaskType ||
          (item.type === "manual cleaning" ? "custom" : item.type),
        isCustomTask:
          item.isCustomTask === true ||
          String(item.cleaningTaskType || "").toLowerCase() === "custom",
        breakTimeAdded: item?.breakTimeAdded || 0,
        breaksIncluded: item?.breaksIncluded || [],
        breakSummary: item?.breakSummary || null,
      };
    });
  };

  const fetchWorkOrdersByRoom = async (room) => {
    try {
      setIsRoomLoading(true);
      setRoomError(null);

      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateKey = `${year}-${month}-${day}`;

      const response = await getAllWorkOrdersByRoom(room, dateKey, dateKey);

      const processedItems = transformTimelineItems(
        response.data.data,
        dateKey,
      );

      setRoomWorkOrders(processedItems);
    } catch (err) {
      console.error("Fetch failed:", err.response || err);
      setRoomError("Failed to fetch work orders for room.");
      if (err.response?.status !== 401) {
        toast.error(
          `Failed to fetch work orders for ${room} on ${selectedDate.toLocaleDateString()}.`,
          { duration: 4000, position: "top-right" },
        );
      }
      setRoomWorkOrders([]);
    } finally {
      setIsRoomLoading(false);
    }
  };

  const fetchKPIWorkOrders = async (room, startDate, endDate) => {
    try {
      setIsLoading(true);
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, "0");
      const day = String(startDate.getDate()).padStart(2, "0");
      const formattedStartDate = `${year}-${month}-${day}`;
      const endYear = endDate.getFullYear();
      const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
      const endDay = String(endDate.getDate()).padStart(2, "0");
      const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;
      const response = await getKPIWorkOrders(
        room === "ALL" ? "" : room,
        formattedStartDate,
        formattedEndDate,
      );
      setKpiData(response);
    } catch (err) {
      console.error("Failed to fetch KPI data:", err);
      if (err.response?.status !== 401) {
        toast.error(`Failed to fetch KPI data for ${room}.`, {
          duration: 4000,
          position: "top-right",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSelectedScheduleKPI = useCallback(async () => {
    try {
      const dateKey = formatDateKey(selectedDate);
      const response = await getKPIWorkOrders(selectedRoom, dateKey, dateKey);
      const baselineQty =
        response.selectedScheduleBaselineQty ??
        (response.selectedScheduleBaselineLocked
          ? response.originalKpiBaselineQty
          : null);
      setScheduleKpiData({
        currentScheduledPlannedQty:
          response.currentScheduledPlannedQty ??
          response.plannedVsActual?.planned ??
          0,
        originalKpiBaselineQty: baselineQty,
        actualCompletedQty:
          response.actualCompletedQty ?? response.plannedVsActual?.actual ?? 0,
        performanceRate:
          response.performanceRate ??
          (response.plannedVsActual?.planned > 0
            ? ((response.plannedVsActual?.actual || 0) /
                response.plannedVsActual.planned) *
              100
            : 0),
        baselineLocked: Boolean(response.selectedScheduleBaselineLocked),
      });
    } catch (err) {
      console.error("Failed to fetch schedule KPI summary:", err);
    }
  }, [selectedDate, selectedRoom]);

  useEffect(() => {
    fetchKPIWorkOrders(kpiRoom, selectedDate, selectedDate);
  }, [kpiRoom, selectedDate]);

  useEffect(() => {
    fetchSelectedScheduleKPI();
  }, [fetchSelectedScheduleKPI]);

  // Initialize data
  const initializeData = () => {
    const workOrdersByDate = {};
    const unassignedWOs = [];

    apiUnassignedWorkOrders.forEach((wo) => {
      let scheduledTime = "00:00";
      let creationDate = null;
      if (wo.scheduledHour) {
        const regex =
          /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)$/i;
        const match = wo.scheduledHour.match(regex);
        if (match) {
          let [, month, day, year, hours, minutes, seconds, period] = match;
          hours = parseInt(hours, 10);
          if (period.toUpperCase() === "PM" && hours !== 12) hours += 12;
          if (period.toUpperCase() === "AM" && hours === 12) hours = 0;
          const dateObj = new Date(
            `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hours
              .toString()
              .padStart(2, "0")}:${minutes}:${seconds}`,
          );
          if (!isNaN(dateObj)) {
            scheduledTime = `${hours.toString().padStart(2, "0")}:${minutes}`;
            creationDate = dateObj.toISOString().split("T")[0];
          }
        }
      }

      const validRooms = [
        "Room 1",
        "Room 2",
        "Room 3",
        "Room Mix 4-1",
        "Room Mix 4-2",
        "Room Mix 4-3",
        "Room 5 Bags",
        "Room 6 Pouch",
        "Bulk Zone",
        "Manual Zone",
      ];
      const normalizedRoom = wo.room && wo.room.includes("Room") ? wo.room : "";
      const room = validRooms.includes(normalizedRoom) ? normalizedRoom : "";

      const allergen = normalizeAllergen(wo.allergens);
      const label = resolveWorkOrderLabel(wo);

      unassignedWOs.push({
        _id: wo._id,
        woNumber: wo.woNumber,
        projectNumber: wo.projectNumber || "-",
        sku: wo.sku || null,
        title: wo.title,
        label,
        labelCategory: label,
        allergen,
        netWeight:
          wo.netWeight && wo.netWeight !== "0.0000000000"
            ? `${parseFloat(wo.netWeight).toFixed(2)} ${
                wo.netWeightCustom || ""
              }`
            : "",
        plannedQty: Number(wo.plannedQty) || 0,
        actualQty: Number(wo.actualQty) || 0,
        currentErpagQty: Number(wo.currentErpagQty ?? wo.plannedQty) || 0,
        kpiPlannedQty:
          wo.kpiPlannedQty === null || wo.kpiPlannedQty === undefined
            ? null
            : Number(wo.kpiPlannedQty) || 0,
        kpiQtyLocked: Boolean(wo.kpiQtyLocked),
        status:
          wo.status?.toLowerCase() === "on hold"
            ? "LAB_HOLD"
            : (wo.status || "").toLowerCase(),
        room: room,
        scheduledTime,
        warehouse: wo.warehouse || "Spice Factory",
        duration: wo.duration || 60,
        lotNumber: wo.lotNumber || null,
        assignedDate: null,
        createdAt: wo.createdAt ? new Date(wo.createdAt) : null,
        isArchived: false,
        slot: wo.slot || null,
        totalDurationMinutes: wo.totalDurationMinutes || wo.duration || 60,
        markedAsCompletedLocally: wo.markedAsCompletedLocally || false,
      });
    });

    const dateKey = formatDateKey(selectedDate);
    workOrdersByDate[dateKey] = {
      "Room 1": [],
      "Room 2": [],
      "Room 3": [],
      "Room Mix 4-1": [],
      "Room Mix 4-2": [],
      "Room Mix 4-3": [],
      "Room 5 Bags": [],
      "Room 6 Pouch": [],
      "Bulk Zone": [],
      "Manual Zone": [],
    };
    roomWorkOrders.forEach((wo) => {
      if (wo.date === dateKey) {
        workOrdersByDate[dateKey][wo.room].push(wo);
      }
    });

    return { workOrdersByDate, unassignedWOs };
  };

  // Initialize data on mount
  useEffect(() => {
    const { workOrdersByDate, unassignedWOs } = initializeData();
    setWorkOrdersByDate(workOrdersByDate);
    setUnassignedWOs(unassignedWOs);
    setSelectedDate(new Date());
    fetchUnassignedWorkOrders();
  }, []);

  useEffect(() => {
    fetchWorkOrdersByRoom(selectedRoom);
  }, [selectedRoom, selectedDate]);

  useEffect(() => {
    const { workOrdersByDate, unassignedWOs } = initializeData();
    setWorkOrdersByDate(workOrdersByDate);
    setUnassignedWOs(unassignedWOs);
  }, [apiUnassignedWorkOrders]);

  useEffect(() => {
    const fetchPostCleaningConfig = async () => {
      try {
        const response = await getPostCleaningConfig();
        if (response.success) {
          setPostCleaningConfigs(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch post-cleaning config", err);
        toast.error("Failed to load post-cleaning schedule.", {
          position: "top-right",
        });
      }
    };
    fetchPostCleaningConfig();
  }, []);

  useEffect(() => {
    const fetchCleaningTaskConfig = async () => {
      try {
        const response = await getCleaningTaskConfig();
        if (response?.data?.data) {
          setCleaningTaskConfig(response.data.data);
          setCleaningConfigDraft(response.data.data);
          setCustomTaskDuration(response.data.data.customMinutes ?? 30);
          setCleaningTaskError(null);
        }
      } catch (err) {
        console.error("Failed to fetch cleaning task config", err);
        setCleaningTaskError("Failed to load cleaning task settings.");
      }
    };
    fetchCleaningTaskConfig();
  }, []);

  useEffect(() => {
    const fetchRoomAllergenPolicies = async () => {
      try {
        const result = await getRoomAllergenPolicies();
        const fetchedPolicies = normalizeRoomAllergenPolicies(
          result?.data?.roomAllergenPolicies,
        );
        setRoomAllergenPolicies(fetchedPolicies);
        setRoomAllergenDraft(fetchedPolicies);
      } catch (err) {
        console.error("Failed to fetch room allergen policies", err);
      }
    };

    fetchRoomAllergenPolicies();
  }, []);

  useEffect(() => {
    if (!isEditingCleaningConfig) {
      setCleaningConfigDraft(cleaningTaskConfig);
    }
  }, [cleaningTaskConfig, isEditingCleaningConfig]);

  const reorderWorkOrdersInRoom = async (room, date, orderedItemsOrIds) => {
    try {
      setIsLoading(true);

      const updated = await reorderWorkOrders(room, date, orderedItemsOrIds);
      const normalizedTimeline = transformTimelineItems(updated.data || [], date);

      setRoomWorkOrders(normalizedTimeline);
      setWorkOrdersByDate((prev) => {
        const next = { ...prev };
        if (!next[date]) {
          next[date] = {
            "Room 1": [],
            "Room 2": [],
            "Room 3": [],
            "Room Mix 4-1": [],
            "Room Mix 4-2": [],
            "Room Mix 4-3": [],
            "Room 5 Bags": [],
            "Room 6 Pouch": [],
            "Bulk Zone": [],
            "Manual Zone": [],
          };
        }
        next[date][room] = normalizedTimeline;
        return next;
      });

      toast.success("Order updated – schedule recalculated!", {
        duration: 4000,
        position: "top-right",
      });

      await Promise.all([
        refreshGlobalKPIs(),
        fetchSelectedScheduleKPI(),
        fetchUnassignedWorkOrders(),
      ]);
      return normalizedTimeline;
    } catch (err) {
      console.error("Reorder failed:", err);

      let message = "Failed to reorder work orders";
      if (err.message?.includes("Invalid reorder request")) {
        message = "Invalid selection or order mismatch – please try again";
      } else if (err.message?.includes("not found")) {
        message = "Room or work orders not found";
      }

      toast.error(message, {
        duration: 5000,
        position: "top-right",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const checkForOvertime = (room, workOrders, cleaningConfigs) => {
    console.log("cleaningConfigs", cleaningConfigs);
    const config = cleaningConfigs.find((c) => c.room.includes(room));
    if (!config || !config.startTime) return null;

    const [cleanH, cleanM] = config.startTime.split(":").map(Number);
    const cleaningStartMinutes = cleanH * 60 + cleanM;
    console.log("cleanH", cleanH);
    console.log("cleanM", cleanM);
    console.log("cleaningStartMinutes", cleaningStartMinutes);
    let latestEndMinutes = 0;

    const workOrdersLength = workOrders.length;
    const lastWorkOrder = workOrders[workOrdersLength - 2];
    const endPart = lastWorkOrder?.slot.split(" - ")[1];

    console.log("lastWorkOrder", lastWorkOrder);
    const match = endPart?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();

    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    latestEndMinutes = hours * 60 + minutes;

    console.log("match", match);
    console.log("latestEndMinutes", latestEndMinutes);
    if (latestEndMinutes > cleaningStartMinutes) {
      const h = Math.floor(latestEndMinutes / 60)
        .toString()
        .padStart(2, "0");
      const m = (latestEndMinutes % 60).toString().padStart(2, "0");
      return {
        room,
        cleaningStart: config.startTime,
        lastWorkEnd: `${h}:${m}`,
      };
    }

    return null;
  };

  const handleRefresh = async () => {
    setIsSyncing(true);
    try {
      const result = await refreshWorkOrders();

      if (result.success) {
        const savedLines = result.linesSaved || 0;
        const processed = result.workOrdersProcessed || 0;

        if (savedLines > 0) {
          toast.success(
            `Sync successful! ${savedLines} work order line(s) updated from ERPAG.`,
            { duration: 5000, position: "top-right" },
          );
        } else {
          toast.success(
            processed > 0
              ? "Sync completed – no new changes to save."
              : "Sync completed – no recent changes found.",
            { duration: 4000, position: "top-right" },
          );
        }

        // Refresh UI data after successful sync
        await Promise.all([
          fetchUnassignedWorkOrders(),
          fetchWorkOrdersByRoom(selectedRoom),
          refreshGlobalKPIs(),
          fetchSelectedScheduleKPI(),
        ]);
      } else {
        throw new Error(result.message || "Sync failed");
      }
    } catch (err) {
      console.error("Refresh Work Orders error:", err);

      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to sync work orders from ERPAG.";

      toast.error(message, {
        duration: 6000,
        position: "top-right",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLockScheduleBaseline = async () => {
    setIsLockingScheduleBaseline(true);
    try {
      const dateKey = formatDateKey(selectedDate);
      const result = await lockScheduleKpiBaseline(selectedRoom, dateKey);
      toast.success(
        result?.message || "Schedule KPI baseline locked successfully",
        { duration: 4000, position: "top-right" },
      );
      await Promise.all([
        fetchSelectedScheduleKPI(),
        fetchKPIWorkOrders(kpiRoom, selectedDate, selectedDate),
      ]);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.baseline) {
        toast.success(
          err.response.data.message || "Schedule KPI baseline is already locked.",
          { duration: 4000, position: "top-right" },
        );
        await fetchSelectedScheduleKPI();
        return;
      }
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to lock schedule KPI baseline.";
      toast.error(message, { duration: 5000, position: "top-right" });
    } finally {
      setIsLockingScheduleBaseline(false);
    }
  };

  const handleArchiveWorkOrder = (wo) => {
    const dateKey = wo.date;
    setWorkOrdersByDate((prev) => ({
      ...prev,
      [dateKey]: {
        ...prev[dateKey],
        [wo.room]: prev[dateKey][wo.room].map((item) =>
          item.woNumber === wo.woNumber ? { ...item, isArchived: true } : item,
        ),
      },
    }));
    setRoomWorkOrders((prev) =>
      prev.map((item) =>
        item.woNumber === wo.woNumber && item.room === selectedRoom
          ? { ...item, isArchived: true }
          : item,
      ),
    );
    toast.success(`Work order ${wo.woNumber} archived successfully!`, {
      duration: 4000,
      position: "top-right",
    });
  };

  const kpiMetrics = useMemo(
    () => ({
      room: kpiRoom,
      completionRate: kpiData.completionRate,
      plannedQty: kpiData.plannedVsActual.planned,
      actualQty: kpiData.plannedVsActual.actual,
      roomUtilization: kpiData.roomUtilization,
    }),
    [kpiData, kpiRoom],
  );

  const allUnassignedWorkOrders = useMemo(() => {
    const result = unassignedWOs
      .filter((wo) => wo.room === "")
      .sort((a, b) => {
        const riskA = getAllergenRiskLevel(a.allergen);
        const riskB = getAllergenRiskLevel(b.allergen);
        return riskA - riskB;
      });
    return result;
  }, [unassignedWOs]);

  // const canEditCleaningConfig = canAssignWorkOrders && !isReadOnly;
  const canEditCleaningConfig = true; // Unlocked as requested

  const handleCleaningConfigChange = (field, value) => {
    setCleaningConfigDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveCleaningConfig = async () => {
    if (!canEditCleaningConfig || isSavingCleaningConfig) return;

    const payload = {
      weeklyMinutes: Number(cleaningConfigDraft.weeklyMinutes),
      monthlyMinutes: Number(cleaningConfigDraft.monthlyMinutes),
      trimesterMinutes: Number(cleaningConfigDraft.trimesterMinutes),
      customMinutes: Number(customTaskDuration),
    };

    const isValid =
      Number.isFinite(payload.weeklyMinutes) &&
      payload.weeklyMinutes > 0 &&
      Number.isFinite(payload.monthlyMinutes) &&
      payload.monthlyMinutes > 0 &&
      Number.isFinite(payload.trimesterMinutes) &&
      payload.trimesterMinutes > 0 &&
      Number.isFinite(payload.customMinutes) &&
      payload.customMinutes > 0 &&
      payload.customMinutes <= 24 * 60;

    if (!isValid) {
      const message =
        "Weekly, monthly, and trimester minutes must be positive numbers, and custom task minutes must be between 1 and 1440.";
      setCleaningTaskError(message);
      toast.error(message, {
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    try {
      setIsSavingCleaningConfig(true);
      const response = await updateCleaningTaskConfig(payload);
      const updated = response?.data?.data || payload;
      setCleaningTaskConfig(updated);
      setCleaningConfigDraft(updated);
      setCustomTaskDuration(updated.customMinutes ?? payload.customMinutes);
      setCleaningTaskError(null);
      setIsEditingCleaningConfig(false);
      toast.success("QC cleaning task durations updated.", {
        duration: 3000,
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to update cleaning task config", err);
      const message =
        err?.response?.data?.message ||
        "Failed to update cleaning task settings.";
      setCleaningTaskError(message);
      if (err?.response?.status !== 401) {
        toast.error(message, {
          duration: 4000,
          position: "top-right",
        });
      }
    } finally {
      setIsSavingCleaningConfig(false);
    }
  };

  const handleStartCleaningConfigEdit = () => {
    setCleaningConfigDraft(cleaningTaskConfig);
    setCustomTaskDuration(cleaningTaskConfig.customMinutes ?? 30);
    setCleaningTaskError(null);
    setIsEditingCleaningConfig(true);
  };

  const handleCancelCleaningConfigEdit = () => {
    setCleaningConfigDraft(cleaningTaskConfig);
    setCustomTaskDuration(cleaningTaskConfig.customMinutes ?? 30);
    setCleaningTaskError(null);
    setIsEditingCleaningConfig(false);
  };

  const cleaningTasks = useMemo(
    () => [
      {
        id: "weekly",
        title: "Weekly Cleaning",
        durationMinutes: cleaningTaskConfig.weeklyMinutes,
      },
      {
        id: "monthly",
        title: "Monthly Cleaning",
        durationMinutes: cleaningTaskConfig.monthlyMinutes,
      },
      {
        id: "trimester",
        title: "Trimester Cleaning",
        durationMinutes: cleaningTaskConfig.trimesterMinutes,
      },
      {
        id: "custom",
        title: "Custom Task",
        durationMinutes: Number(customTaskDuration) || 30,
      },
    ],
    [cleaningTaskConfig, customTaskDuration],
  );

  const scheduledCleaningTypes = useMemo(() => {
    const types = new Set();
    (roomWorkOrders || []).forEach((item) => {
      if (item?.type !== "manual cleaning") return;
      const title = String(item.title || "").toLowerCase();
      if (title.includes("weekly")) types.add("weekly");
      if (title.includes("monthly")) types.add("monthly");
      if (title.includes("trimester")) types.add("trimester");
    });
    return types;
  }, [roomWorkOrders]);

  const handleDragStart = (e, wo, sourceRoom) => {
    const id = sourceRoom === "unassigned" ? wo._id : wo.woNumber;
    e.dataTransfer.setData("woId", id);
    e.dataTransfer.setData("sourceRoom", sourceRoom || "unassigned");
    e.dataTransfer.effectAllowed = "move";
  };

  const handleCleaningTaskDragStart = (e, task) => {
    e.dataTransfer.setData("dragType", "qc-cleaning");
    e.dataTransfer.setData("taskType", task.id);
    if (task.id === "custom") {
      const parsedDuration = Math.max(1, Number(customTaskDuration) || 30);
      e.dataTransfer.setData("customTaskTitle", "Custom Task");
      e.dataTransfer.setData("customTaskDuration", String(parsedDuration));
    }
    e.dataTransfer.effectAllowed = "copy";
  };

  const getDropTargetInfo = (e) => {
    const row = e?.target?.closest?.("tr[data-row-id]");
    if (!row) return null;
    if (row.dataset.rowDraggable !== "true") return null;
    const rect = row.getBoundingClientRect();
    const position =
      e.clientY < rect.top + rect.height / 2 ? "before" : "after";
    return {
      id: row.dataset.rowId,
      type: row.dataset.rowType,
      position,
    };
  };

  const buildOrderedItemsFromTimeline = (items) => {
    return (items || [])
      .filter(
        (item) =>
          (item.type === "workorder" ||
            item.type === "work order" ||
            item.type === "manual cleaning") &&
          item._id,
      )
      .map((item) => ({
        id: item._id,
        type: item.type === "manual cleaning" ? "cleaning" : "workorder",
      }));
  };

  const moveOrderedItem = (items, movingId, targetId, position) => {
    if (!movingId || !targetId) return items;
    const list = [...items];
    const fromIndex = list.findIndex(
      (item) => String(item.id) === String(movingId),
    );
    const toIndex = list.findIndex(
      (item) => String(item.id) === String(targetId),
    );
    if (fromIndex === -1 || toIndex === -1) return items;
    if (fromIndex === toIndex) return items;
    const [moved] = list.splice(fromIndex, 1);
    const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
    const insertIndex =
      position === "after" ? adjustedToIndex + 1 : adjustedToIndex;
    list.splice(insertIndex, 0, moved);
    return list;
  };

  const handleDrop = async (e, targetRoom, targetDate) => {
    e.preventDefault();
    setIsDraggingOver((prev) => ({
      ...prev,
      [`${targetDate}_${targetRoom}`]: false,
    }));

    const dropTarget = getDropTargetInfo(e);
    const dragType = e.dataTransfer.getData("dragType");
    const woId = e.dataTransfer.getData("woId");
    const sourceRoom = e.dataTransfer.getData("sourceRoom");
    const targetDateObj = parseLocalDate(targetDate);

    if (
      targetDateObj.getTime() < today.getTime() &&
      !isSameDay(targetDateObj, today)
    ) {
      if (error?.response?.status !== 401) {
        toast.error("Cannot move work orders to past dates.", {
          duration: 4000,
          position: "top-right",
        });
      }
      return;
    }

    const normalizedTargetRoom = targetRoom;

    if (dragType === "qc-cleaning") {
      try {
        setIsLoading(true);
        const taskType = e.dataTransfer.getData("taskType");
        const customOptions =
          taskType === "custom"
            ? {
                customTitle:
                  e.dataTransfer.getData("customTaskTitle") || "Custom Task",
                durationMinutes:
                  Number(e.dataTransfer.getData("customTaskDuration")) || 30,
              }
            : {};
        const response = await addCleaningTaskToSchedule(
          normalizedTargetRoom,
          targetDate,
          taskType,
          customOptions,
        );
        const updatedData = transformTimelineItems(
          response?.data?.data || [],
          targetDate,
        );

        if (dropTarget && response?.data?.task?._id) {
          const orderedItems = buildOrderedItemsFromTimeline(
            response?.data?.data || [],
          );
          const movedItems = moveOrderedItem(
            orderedItems,
            response.data.task._id,
            dropTarget.id,
            dropTarget.position,
          );
          if (movedItems !== orderedItems) {
            const reordered = await reorderWorkOrdersInRoom(
              normalizedTargetRoom,
              targetDate,
              movedItems,
            );
            if (reordered) {
              return;
            }
          }
        }

        setWorkOrdersByDate((prev) => {
          const updated = { ...prev };
          if (!updated[targetDate]) {
            updated[targetDate] = {
              "Room 1": [],
              "Room 2": [],
              "Room 3": [],
              "Room Mix 4-1": [],
              "Room Mix 4-2": [],
              "Room Mix 4-3": [],
              "Room 5 Bags": [],
              "Room 6 Pouch": [],
              "Bulk Zone": [],
              "Manual Zone": [],
            };
          }
          updated[targetDate][normalizedTargetRoom] = updatedData;
          return updated;
        });

        if (
          normalizedTargetRoom === selectedRoom &&
          targetDate === formatDateKey(selectedDate)
        ) {
          setRoomWorkOrders(updatedData);
        }

        toast.success("Cleaning task added to schedule.", {
          duration: 3000,
          position: "top-right",
        });
      } catch (err) {
        console.error("Failed to add cleaning task:", err);
        const message =
          err?.response?.data?.message ||
          "Failed to add cleaning task to schedule.";
        if (err?.response?.status !== 401) {
          toast.error(message, {
            duration: 4000,
            position: "top-right",
          });
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    let wo;

    if (sourceRoom === "unassigned") {
      wo = unassignedWOs.find((w) => w._id === woId);
      if (!wo) {
        console.error("Work order not found in unassignedWOs:", {
          woId,
          unassignedWOs: unassignedWOs.map((w) => ({
            _id: w._id,
            woNumber: w.woNumber,
          })),
        });
        if (error?.response?.status !== 401) {
          toast.error("Work order not found.", {
            duration: 4000,
            position: "top-right",
          });
        }
        return;
      }

      if (!wo._id) {
        console.error("Work order has no _id:", wo);
        if (error.response?.status !== 401) {
          toast.error("Invalid work order ID.", {
            duration: 4000,
            position: "top-right",
          });
        }
        return;
      }

      if (!validateRoomAllergenRule(wo, normalizedTargetRoom, targetDate)) {
        return;
      }

      const prevUnassignedWOs = [...unassignedWOs];
      const prevWorkOrdersByDate = { ...workOrdersByDate };

      // Build updatedWOs first
      const existingWOs =
        workOrdersByDate[targetDate]?.[normalizedTargetRoom] || [];
      const updatedWOs = [
        ...existingWOs,
        {
          ...wo,
          status: "planned",
          room: normalizedTargetRoom,
          date: targetDate,
          assignedDate: targetDate,
        },
      ].sort((a, b) => {
        const riskA = getAllergenRiskLevel(a.allergen);
        const riskB = getAllergenRiskLevel(b.allergen);
        if (riskA !== riskB) return riskA - riskB;
        return (a.scheduledTime || "00:00").localeCompare(
          b.scheduledTime || "00:00",
        );
      });

      // Apply optimistic UI update
      setUnassignedWOs((prev) => prev.filter((item) => item._id !== woId));
      setWorkOrdersByDate((prev) => {
        const updated = { ...prev };
        if (!updated[targetDate]) {
          updated[targetDate] = {
            "Room 1": [],
            "Room 2": [],
            "Room 3": [],
            "Room Mix 4-1": [],
            "Room Mix 4-2": [],
            "Room Mix 4-3": [],
            "Room 5 Bags": [],
            "Room 6 Pouch": [],
            "Bulk Zone": [],
            "Manual Zone": [],
          };
        }
        updated[targetDate][normalizedTargetRoom] = updatedWOs;
        return updated;
      });

      try {
        setIsLoading(true);
        const response = await assignRoom(
          wo._id,
          normalizedTargetRoom,
          targetDate,
        );

        console.log("selectedRoom", selectedRoom);
        console.log("normalizedTargetRoom", normalizedTargetRoom);

        if (response.data.message === "Room assigned successfully") {
          if (dropTarget && response?.data?.data) {
            const orderedItems = buildOrderedItemsFromTimeline(
              response.data.data,
            );
            const movedItems = moveOrderedItem(
              orderedItems,
              wo._id,
              dropTarget.id,
              dropTarget.position,
            );
            if (movedItems !== orderedItems) {
              await reorderWorkOrdersInRoom(
                normalizedTargetRoom,
                targetDate,
                movedItems,
              );
            }
          }

          await Promise.all([
            fetchUnassignedWorkOrders(),
            fetchWorkOrdersByRoom(selectedRoom),
            refreshGlobalKPIs(),
            fetchSelectedScheduleKPI(),
          ]);

          const overtimeAfterAssign = checkForOvertime(
            normalizedTargetRoom,
            roomWorkOrders,
            postCleaningConfigs,
          );

          if (overtimeAfterAssign) {
            setOvertimeDetails(overtimeAfterAssign);
            setShowOvertimeModal(true);
          }

          toast.success("Work order assigned successfully!", {
            duration: 4000,
            position: "top-right",
          });
        } else {
          throw new Error(
            "Unexpected API response: " + JSON.stringify(response.data),
          );
        }
      } catch (error) {
        // Revert optimistic UI
        setUnassignedWOs(prevUnassignedWOs);
        setWorkOrdersByDate(prevWorkOrdersByDate);

        console.error("Error assigning room:", error);

        const apiErrorMsg = error.response?.data?.error;
        const apiMessage = error.response?.data?.message;
        const fallbackMsg = "Failed to assign room. Please try again.";

        let displayMessage = apiErrorMsg || apiMessage || fallbackMsg;

        if (
          apiErrorMsg &&
          (apiErrorMsg.includes("Missing product fields") ||
            apiErrorMsg.includes("cleaning_level") ||
            apiErrorMsg.includes("machine_tool") ||
            apiErrorMsg.includes("productivity_level"))
        ) {
          setPendingAssignment({
            wo,
            targetRoom: normalizedTargetRoom,
            targetDate,
          });
          setAssignError(error.response.data);
          setShowAssignErrorModal(true);

          toast.error(
            "Missing required product info. Please fill in the details to assign.",
            {
              duration: 6000,
              position: "top-right",
            },
          );
        } else {
          toast.error(displayMessage, {
            duration: 6000,
            position: "top-right",
          });
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      const sourceDate = Object.keys(workOrdersByDate).find((date) =>
        workOrdersByDate[date][sourceRoom]?.some((w) => w.woNumber === woId),
      );

      wo = workOrdersByDate[sourceDate]?.[sourceRoom]?.find(
        (w) => w.woNumber === woId,
      );

      if (!wo) {
        console.error("Work order not found in workOrdersByDate:", {
          woId,
          sourceRoom,
          sourceDate,
          workOrdersByDate: workOrdersByDate[sourceDate]?.[sourceRoom]?.map(
            (w) => ({ _id: w._id, woNumber: w.woNumber }),
          ),
        });
        if (error?.response?.status !== 401) {
          toast.error("Work order not found.", {
            duration: 4000,
            position: "top-right",
          });
        }
        return;
      }

      if (sourceDate === targetDate && sourceRoom === normalizedTargetRoom) {
        return;
      }

      if (!validateRoomAllergenRule(wo, normalizedTargetRoom, targetDate)) {
        return;
      }

      const prevWorkOrdersByDate = { ...workOrdersByDate };

      setWorkOrdersByDate((prev) => ({
        ...prev,
        [sourceDate]: {
          ...prev[sourceDate],
          [sourceRoom]: prev[sourceDate][sourceRoom].filter(
            (item) => item.woNumber !== woId,
          ),
        },
      }));

      setWorkOrdersByDate((prev) => {
        const existingWOs = prev[targetDate]?.[normalizedTargetRoom] || [];
        const updatedWOs = [
          ...existingWOs,
          {
            ...wo,
            status: "planned",
            room: normalizedTargetRoom,
            date: targetDate,
            assignedDate: targetDate,
          },
        ].sort((a, b) => {
          const riskA = getAllergenRiskLevel(a.allergen);
          const riskB = getAllergenRiskLevel(b.allergen);
          if (riskA !== riskB) return riskA - riskB;
          return (a.scheduledTime || "00:00").localeCompare(
            b.scheduledTime || "00:00",
          );
        });
        return {
          ...prev,
          [targetDate]: {
            ...(prev[targetDate] || {
              "Room 1": [],
              "Room 2": [],
              "Room 3": [],
              "Room Mix 4-1": [],
              "Room Mix 4-2": [],
              "Room Mix 4-3": [],
              "Room 5 Bags": [],
              "Room 6 Pouch": [],
              "Bulk Zone": [],
              "Manual Zone": [],
            }),
            [normalizedTargetRoom]: updatedWOs,
          },
        };
      });

      try {
        setIsLoading(true);
        const response = await assignRoom(
          wo._id || wo.woNumber,
          normalizedTargetRoom,
          targetDate,
        );

        if (response.data.message === "Room assigned successfully") {
          await Promise.all([
            fetchUnassignedWorkOrders(),
            fetchWorkOrdersByRoom(selectedRoom),
            refreshGlobalKPIs(),
            fetchSelectedScheduleKPI(),
          ]);

          console.log("selectedRoom", selectedRoom);
          console.log("normalizedTargetRoom", normalizedTargetRoom);

          const overtimeAfterAssign = checkForOvertime(
            normalizedTargetRoom,
            roomWorkOrders,
            postCleaningConfigs,
          );

          if (overtimeAfterAssign) {
            setOvertimeDetails(overtimeAfterAssign);
            setShowOvertimeModal(true);
          }

          toast.success("Work order reassigned successfully!", {
            duration: 4000,
            position: "top-right",
          });
        } else {
          throw new Error(
            "Unexpected API response: " + JSON.stringify(response.data),
          );
        }
      } catch (error) {
        setWorkOrdersByDate(prevWorkOrdersByDate);

        console.error("Error reassigning room:", {
          workOrderId: wo._id || wo.woNumber,
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });

        if (error?.response?.status !== 401) {
          const apiErrorMsg = error.response?.data?.error;
          const apiMessage = error.response?.data?.message;
          const fallbackMsg = "Failed to reassign room. Changes reverted.";

          let displayMessage = apiErrorMsg || apiMessage || fallbackMsg;

          toast.error(displayMessage, {
            duration: 10000,
            position: "top-right",
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRemoveCleaningTask = async (taskId) => {
    if (!taskId) return;

    try {
      setIsLoading(true);
      const response = await removeCleaningTaskFromSchedule(taskId);
      const dateKey = formatDateKey(selectedDate);
      const updatedData = transformTimelineItems(
        response?.data?.data || [],
        dateKey,
      );

      if (!updatedData || updatedData.length === 0) {
        console.warn(
          "removeCleaningTask returned empty timeline after removal",
          {
            taskId,
            responseData: response?.data,
          },
        );
      }

      setWorkOrdersByDate((prev) => {
        const updated = { ...prev };
        if (updated[dateKey]) {
          updated[dateKey][selectedRoom] = updatedData;
        }
        return updated;
      });

      setRoomWorkOrders(updatedData);

      toast.success("Cleaning task removed.", {
        duration: 3000,
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to remove cleaning task:", err);

      const msg =
        err.response?.data?.message ||
        "Failed to remove cleaning task. Please refresh to check status.";

      toast.error(msg, {
        duration: 5000,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameCleaningTask = async (taskId, title) => {
    if (!taskId || !title) return;

    try {
      setIsLoading(true);
      const response = await renameCleaningTaskInSchedule(taskId, title);
      const dateKey = formatDateKey(selectedDate);
      const updatedData = transformTimelineItems(
        response?.data?.data || [],
        dateKey,
      );

      setWorkOrdersByDate((prev) => {
        const updated = { ...prev };
        if (updated[dateKey]) {
          updated[dateKey][selectedRoom] = updatedData;
        }
        return updated;
      });

      setRoomWorkOrders(updatedData);
      toast.success("Custom task renamed.", {
        duration: 3000,
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to rename custom task:", err);
      const msg =
        err.response?.data?.message ||
        "Failed to rename custom task. Please refresh to check status.";
      toast.error(msg, {
        duration: 5000,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const retryAssignRoom = async (selectedValues) => {
    if (!pendingAssignment) return;
    const { wo, targetRoom, targetDate } = pendingAssignment;

    try {
      setIsLoading(true);
      const manualAllowedRooms = ["Room 1", "Room 2", "Room 3", "Room 5 Bags"];
      if (
        selectedValues.productivity_level === "Manual" &&
        !manualAllowedRooms.includes(targetRoom)
      ) {
        toast.error(
          "Manual productivity is only allowed in Room 1, Room 2, Room 3, or Room 5 Bags.",
          { duration: 5000, position: "top-right" },
        );
        setIsLoading(false);
        return;
      }
      if (
        selectedValues.machine_tool === "Long-for-Bagging" &&
        targetRoom !== "Room 5 Bags"
      ) {
        toast.error("Long-for-Bagging is only allowed in Room 5 Bags.", {
          duration: 5000,
          position: "top-right",
        });
        setIsLoading(false);
        return;
      }

      const updatePayload = {
        ids: [wo.sku],
        ...selectedValues,
      };

      await updateProduct(updatePayload);
      const assignResponse = await assignRoom(wo._id, targetRoom, targetDate);

      if (assignResponse.data.message === "Room assigned successfully") {
        await Promise.all([
          fetchUnassignedWorkOrders(),
          fetchWorkOrdersByRoom(selectedRoom),
          refreshGlobalKPIs(),
          fetchSelectedScheduleKPI(),
        ]);
        console.log("selectedRoom", selectedRoom);
        console.log("targetRoom", targetRoom);
        const overtimeAfterAssign = checkForOvertime(
          targetRoom,
          roomWorkOrders,
          postCleaningConfigs,
        );
        if (overtimeAfterAssign) {
          setOvertimeDetails(overtimeAfterAssign);
          setShowOvertimeModal(true);
        }
        toast.success("Work order assigned successfully!", {
          duration: 4000,
          position: "top-right",
        });
        setShowAssignErrorModal(false);
        setPendingAssignment(null);
        setAssignError(null);
      }
    } catch (err) {
      console.error("Assignment flow error:", err);
      if (err.response?.status !== 401) {
        toast.error(
          err.response?.data?.message ||
            "Failed to update required fields. Please try again.",
          { duration: 5000, position: "top-right" },
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e, room, date) => {
    e.preventDefault();
    setIsDraggingOver((prev) => ({ ...prev, [`${date}_${room}`]: true }));
  };

  const handleDragLeave = (e, room, date) => {
    e.preventDefault();
    setIsDraggingOver((prev) => ({ ...prev, [`${date}_${room}`]: false }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const dateKey = formatDateKey(date);
    if (!workOrdersByDate[dateKey]) {
      setWorkOrdersByDate((prev) => ({
        ...prev,
        [dateKey]: {
          "Room 1": [],
          "Room 2": [],
          "Room 3": [],
          "Room Mix 4-1": [],
          "Room Mix 4-2": [],
          "Room Mix 4-3": [],
          "Room 5 Bags": [],
          "Room 6 Pouch": [],
          "Bulk Zone": [],
          "Manual Zone": [],
        },
      }));
    }
  };
    const handleDayNavigation = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + (direction === "prev" ? -1 : 1));
    handleDateChange(newDate);
  };

  const handleContextMenuAction = async (wo, action, data) => {
    const dateKey = formatDateKey(selectedDate);
    if (action === "unassign") {
      const prevWorkOrdersByDate = { ...workOrdersByDate };
      const prevUnassignedWOs = [...unassignedWOs];

      // Optimistic: Remove from current room
      setWorkOrdersByDate((prev) => ({
        ...prev,
        [dateKey]: {
          ...prev[dateKey],
          [wo.room]: prev[dateKey][wo.room].filter((w) => w._id !== wo._id),
        },
      }));

      // Optimistic: Add to unassigned
      setUnassignedWOs((prev) => [
        ...prev,
        { ...wo, room: "", assignedDate: null, status: "planned" },
      ]);

      try {
        setIsLoading(true);
        await unassignWorkOrder(wo._id);

        // Refresh both lists
        await Promise.all([
          fetchUnassignedWorkOrders(),
          fetchWorkOrdersByRoom(selectedRoom),
          refreshGlobalKPIs(),
          fetchSelectedScheduleKPI(),
        ]);

        toast.success(`Work order ${wo.woNumber} unassigned!`, {
          duration: 4000,
          position: "top-right",
        });
      } catch (error) {
        // Rollback
        setWorkOrdersByDate(prevWorkOrdersByDate);
        setUnassignedWOs(prevUnassignedWOs);
        toast.error("Failed to unassign. Changes reverted.", {
          duration: 4000,
          position: "top-right",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }
    if (action === "changeStatus") {
      if (["approved", "planned", "in progress", "completed"].includes(data)) {
        setWorkOrdersByDate((prev) => ({
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            [selectedRoom]: prev[dateKey][selectedRoom].map((item) =>
              item.woNumber === wo.woNumber ? { ...item, status: data } : item,
            ),
          },
        }));
        setRoomWorkOrders((prev) =>
          prev.map((item) =>
            item.woNumber === wo.woNumber && item.room === selectedRoom
              ? { ...item, status: data }
              : item,
          ),
        );
        toast.success(`Status updated to ${data}!`, {
          duration: 4000,
          position: "top-right",
        });
      }
    } else if (action === "moveToDate" && data) {
      const targetDate = parseLocalDate(data);
      if (
        targetDate.getTime() < today.getTime() &&
        !isSameDay(targetDate, today)
      ) {
        if (error?.response?.status !== 401) {
          toast.error("Cannot move work orders to past dates.", {
            duration: 4000,
            position: "top-right",
          });
        }
        return;
      }

      // Create a temporary state with initialized target date
      const tempWorkOrdersByDate = {
        ...workOrdersByDate,
        [data]: workOrdersByDate[data] || {
          "Room 1": [],
          "Room 2": [],
          "Room 3": [],
          "Room Mix 4-1": [],
          "Room Mix 4-2": [],
          "Room Mix 4-3": [],
          "Room 5 Bags": [],
          "Room 6 Pouch": [],
          "Bulk Zone": [],
          "Manual Zone": [],
        },
      };

      // Only check allergen restrictions if moving to a different room
      if (wo.room !== selectedRoom) {
        if (!validateRoomAllergenRule(wo, selectedRoom, data)) {
          return;
        }
      }

      // Store previous state for rollback
      const prevWorkOrdersByDate = { ...workOrdersByDate };
      const prevRoomWorkOrders = [...roomWorkOrders];

      // Optimistically update the UI
      setWorkOrdersByDate((prev) => {
        const updatedWOs = [
          ...(tempWorkOrdersByDate[data][selectedRoom] || []),
          {
            ...wo,
            date: data,
            room: selectedRoom,
            assignedDate: data,
          },
        ].sort((a, b) => {
          const riskA = getAllergenRiskLevel(a.allergen);
          const riskB = getAllergenRiskLevel(b.allergen);
          if (riskA !== riskB) return riskA - riskB;
          return (a.scheduledTime || "00:00").localeCompare(
            b.scheduledTime || "00:00",
          );
        });
        return {
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            [selectedRoom]: prev[dateKey][selectedRoom].filter(
              (item) => item.woNumber !== wo.woNumber,
            ),
          },
          [data]: {
            ...tempWorkOrdersByDate[data],
            [selectedRoom]: updatedWOs,
          },
        };
      });
      setRoomWorkOrders((prev) => {
        const remainingWorkOrders = prev.filter(
          (item) => item.woNumber !== wo.woNumber,
        );

        if (data !== dateKey) {
          return remainingWorkOrders;
        }

        return remainingWorkOrders.concat([
          {
            ...wo,
            date: data,
            room: selectedRoom,
            assignedDate: data,
          },
        ]);
      });

      // Call appropriate API based on whether room is changing
      try {
        setIsLoading(true);
        let response;
        if (wo.room === selectedRoom) {
          response = await updateAssignedDate(wo._id, data);
        } else {
          response = await assignRoom(wo._id, selectedRoom, data);
        }
        const responseData = response?.data || response;
        if (
          responseData?.message === "Room assigned successfully" ||
          responseData?.message === "Assigned date updated successfully" ||
          responseData?.success === true
        ) {
          await Promise.all([
            fetchWorkOrdersByRoom(selectedRoom),
            fetchUnassignedWorkOrders(),
            refreshGlobalKPIs(),
            fetchSelectedScheduleKPI(),
          ]);
          // Format date for user-friendly toast
          const formattedDate = parseLocalDate(data).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          toast.success(
            `Work order ${wo.woNumber} moved to ${formattedDate}!`,
            {
              duration: 4000,
              position: "top-right",
            },
          );
        } else {
          throw new Error(
            "Unexpected API response: " + JSON.stringify(responseData),
          );
        }
      } catch (error) {
        // Revert state on failure
        setWorkOrdersByDate(prevWorkOrdersByDate);
        setRoomWorkOrders(prevRoomWorkOrders);
        console.error("Error moving work order to specific date:", {
          workOrderId: wo._id,
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });
        if (error?.response?.status !== 401) {
          toast.error(
            error.response?.data?.message ||
              "Failed to move work order to specific date. Changes reverted.",
            {
              duration: 4000,
              position: "top-right",
            },
          );
        }
      } finally {
        setIsLoading(false);
      }
    } else if (action === "removeTask") {
      setWorkOrdersByDate((prev) => ({
        ...prev,
        [dateKey]: {
          ...prev[dateKey],
          [selectedRoom]: prev[dateKey][selectedRoom].filter(
            (item) => item.woNumber !== wo.woNumber,
          ),
        },
      }));
      setRoomWorkOrders((prev) =>
        prev.filter(
          (item) => item.woNumber !== wo.woNumber || item.room !== selectedRoom,
        ),
      );
      toast.success("Task removed successfully!", {
        duration: 4000,
        position: "top-right",
      });
    }
  };

  const handleHold = (wo, holdType) => {
    const dateKey = wo.date;
    setWorkOrdersByDate((prev) => ({
      ...prev,
      [dateKey]: {
        ...prev[dateKey],
        [wo.room]: prev[dateKey][wo.room].map((item) =>
          item.woNumber === wo.woNumber ? { ...item, status: holdType } : item,
        ),
      },
    }));
    setRoomWorkOrders((prev) =>
      prev.map((item) =>
        item.woNumber === wo.woNumber && item.room === selectedRoom
          ? { ...item, status: holdType }
          : item,
      ),
    );
    toast.success(`Work order ${wo.woNumber} moved to ${holdType}!`, {
      duration: 4000,
      position: "top-right",
    });
  };

  const handleOpenRecurringTaskModal = () => {
    if (!canCreateRecurringTasks) {
      toast.error("You do not have permission to create recurring tasks.", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }
    setIsRecurringTaskModalOpen(true);
  };

  const handleCloseRecurringTaskModal = () => {
    setIsRecurringTaskModalOpen(false);
  };

  const handleOpenRoomAllergenModal = () => {
    if (!canManageRoomAllergenPolicies) {
      toast.error("You do not have permission to edit room allergen rules.", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    setRoomAllergenDraft(roomAllergenPolicies);
    setIsRoomAllergenModalOpen(true);
  };

  const handleCloseRoomAllergenModal = () => {
    setIsRoomAllergenModalOpen(false);
    setIsSavingRoomAllergenPolicies(false);
  };

  const toggleRoomAllergen = (roomName, allergen) => {
    setRoomAllergenDraft((prev) =>
      prev.map((policy) => {
        if (policy.room !== roomName) return policy;

        const hasAllergen = policy.allowedAllergens.includes(allergen);
        const allowedAllergens = hasAllergen
          ? policy.allowedAllergens.filter((item) => item !== allergen)
          : [...policy.allowedAllergens, allergen];

        return { ...policy, allowedAllergens };
      }),
    );
  };

  const setAllergenModeForRoom = (roomName, mode) => {
    setRoomAllergenDraft((prev) =>
      prev.map((policy) => {
        if (policy.room !== roomName) return policy;

        return {
          ...policy,
          allowedAllergens: mode === "all" ? [...ALLERGEN_OPTIONS] : [],
        };
      }),
    );
  };

  const handleSaveRoomAllergenPolicies = async () => {
    if (!canManageRoomAllergenPolicies) {
      toast.error("You do not have permission to save room allergen rules.", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    setIsSavingRoomAllergenPolicies(true);
    try {
      const payload = normalizeRoomAllergenPolicies(roomAllergenDraft);
      const result = await updateRoomAllergenPolicies(payload);
      const savedPolicies = normalizeRoomAllergenPolicies(
        result?.data?.roomAllergenPolicies,
      );

      setRoomAllergenPolicies(savedPolicies);
      setRoomAllergenDraft(savedPolicies);
      setIsRoomAllergenModalOpen(false);
      toast.success("Room allergen rules saved successfully.", {
        duration: 4000,
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to save room allergen policies", err);
      toast.error("Failed to save room allergen rules.", {
        duration: 4000,
        position: "top-right",
      });
    } finally {
      setIsSavingRoomAllergenPolicies(false);
    }
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isRecurringTaskModalOpen) {
        handleCloseRecurringTaskModal();
      }
      if (event.key === "Escape" && isRoomAllergenModalOpen) {
        handleCloseRoomAllergenModal();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isRecurringTaskModalOpen, isRoomAllergenModalOpen]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategoriesList();
        setCategories(response.data || []);
      } catch (err) {
        setCategoriesError("Failed to fetch categories.");
        if (err.response?.status !== 401) {
          toast.error("Failed to fetch categories.", {
            duration: 4000,
            position: "top-right",
          });
        }
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-6 font-sans">
      <Toaster />
      <div className="flex gap-6 mx-auto flex-col md:flex-row">
        <div className="flex-none w-full md:w-64 bg-white p-6 rounded-lg shadow-lg border border-gray-200 h-[calc(100vh-3rem)] overflow-y-auto md:sticky md:top-6 no-scrollbar">
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4 pb-3 border-b border-gray-200 text-gray-800">
              Date
            </h3>
            <SingleDatePicker
              id="datePicker"
              value={selectedDate}
              onChange={handleDateChange}
              minDate={new Date(2025, 0, 1)}
              placeholderText="Select Date"
              className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
              aria-label="Select Date"
            />
            <div className="flex flex-col gap-2 mt-3">
              <div className="flex gap-2">
                <button
                  onClick={() => handleDayNavigation("prev")}
                  className="flex-1 py-2 px-4 rounded bg-gray-300 hover:bg-gray-400 text-gray-900 transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-gray-500 focus:outline-none"
                  aria-label="Previous Day"
                >
                  Previous
                </button>
                <button
                  onClick={() => handleDayNavigation("next")}
                  className="flex-1 py-2 px-4 rounded bg-gray-300 hover:bg-gray-400 text-gray-900 transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-gray-500 focus:outline-none"
                  aria-label="Next Day"
                >
                  Next
                </button>
              </div>
            </div>
            <div className="mt-6">
              {canCreateRecurringTasks ? (
                <button
                  onClick={handleOpenRecurringTaskModal}
                  className="w-full py-2 px-4 rounded bg-black hover:bg-gray-900 text-white transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-gray-500 focus:outline-none"
                  aria-label="Manage Recurring Tasks"
                >
                  Manage Recurring Tasks
                </button>
              ) : (
                <div className="w-full py-2 px-4 rounded bg-gray-300 text-gray-600 text-sm font-medium text-center">
                  Manage Recurring Tasks (No Permission)
                </div>
              )}
            </div>
            <div className="mt-3">
              {canManageRoomAllergenPolicies ? (
                <button
                  onClick={handleOpenRoomAllergenModal}
                  className="w-full py-2 px-4 rounded bg-blue-900  hover:bg-[#365daf] text-white transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-gray-500 focus:outline-none"
                  aria-label="Manage Room Allergen"
                >
                  Manage Room Allergen
                </button>
              ) : (
                <div className="w-full py-2 px-4 rounded bg-gray-300 text-gray-600 text-sm font-medium text-center">
                  Manage Room Allergen (No Permission)
                </div>
              )}
            </div>


          </div>

          <h3 className="text-xl font-semibold mb-4 pb-3 border-b border-gray-200 text-gray-800 mt-6">
            Rooms
          </h3>
          {rooms.map((room) => (
            <div
              key={room}
              className={`py-2 px-2 mb-2 rounded border border-gray-800 bg-black font-semibold text-white cursor-pointer transition-all duration-200 ${
                selectedRoom === room ? "bg-gray-700 text-white" : ""
              } ${
                isDraggingOver[
                  `${formatDateKey(selectedDate)}_${room}`
                ] && !isReadOnly
                  ? "border-2 border-dashed border-gray-900 bg-gray-700"
                  : ""
              }`}
              onClick={() => setSelectedRoom(room)}
              onDrop={
                isReadOnly
                  ? undefined
                  : (e) =>
                      handleDrop(
                        e,
                        room,
                        formatDateKey(selectedDate),
                      )
              }
              onDragOver={
                isReadOnly
                  ? undefined
                  : (e) =>
                      handleDragOver(
                        e,
                        room,
                        formatDateKey(selectedDate),
                      )
              }
              onDragLeave={
                isReadOnly
                  ? undefined
                  : (e) =>
                      handleDragLeave(
                        e,
                        room,
                        formatDateKey(selectedDate),
                      )
              }
              role="button"
              tabIndex={0}
              aria-label={`Select room ${room}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setSelectedRoom(room);
                }
              }}
            >
              {room}
            </div>
          ))}
        </div>

        <div className="flex-1 flex flex-col gap-6 custom-WorkOrderRight ">
          <header className="pb-3 bg-gradient-to-r from-black to-gray-900 text-white p-4 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold m-0">
              Work Order Queue –{" "}
              <span className="font-extrabold">{selectedRoom}</span> –{" "}
              <span className="font-extrabold">
                {selectedDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              {isReadOnly && (
                <span className="text-sm text-gray-300 ml-2 italic">
                  (Historical - Read Only)
                </span>
              )}
            </h2>
<div className="flex gap-2">
            <button
              onClick={handleSyncProducts}
              disabled={isSyncingProducts}
              className={`flex items-center gap-2 py-2 px-4 rounded-lg text-white transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed ${
                isSyncingProducts ? "bg-blue-700 opacity-80" : "bg-blue-600 hover:bg-blue-700"
              }`}
              aria-label="Refresh Products"
            >
              <ArrowPathIcon className={`h-5 w-5 ${isSyncingProducts ? "animate-spin" : ""}`} />
              {isSyncingProducts ? "Syncing..." : "Refresh Products"}
            </button>

            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 py-2 px-4 rounded-lg bg-white text-black hover:bg-gray-100 transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-gray-500 focus:outline-none disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
              disabled={isLoading || isRoomLoading || isSyncing}
              aria-label="Sync Data"
              >
              <ArrowPathIcon
                className={`h-5 w-5 ${isSyncing ? "animate-spin" : ""}`}
                />
              Refresh
            </button>
            <button
              onClick={handleLockScheduleBaseline}
              className="flex items-center gap-2 py-2 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-all duration-200 font-semibold text-sm shadow-sm focus:ring-2 focus:ring-emerald-300 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={
                isLoading ||
                isRoomLoading ||
                isLockingScheduleBaseline ||
                roomWorkOrders.length === 0
              }
              >
              <DocumentCheckIcon className="h-5 w-5" />
              {isLockingScheduleBaseline
                ? "Locking..."
                : "Lock Schedule KPI"}
            </button>
                </div>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold pb-3 border-b border-gray-200 text-gray-800">
                KPI Dashboard ({kpiRoom})
              </h3>
              <div className="flex items-center gap-2">
                <label
                  htmlFor="kpiRoom"
                  className="text-sm font-medium text-gray-700"
                >
                  Room:
                </label>
                <select
                  id="kpiRoom"
                  value={kpiRoom}
                  onChange={(e) => setKpiRoom(e.target.value)}
                  className="py-1 px-2 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
                  aria-label="Select Room for KPIs"
                >
                  <option value="ALL">ALL</option>
                  {rooms.map((room) => (
                    <option key={room} value={room}>
                      {room}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Completion Rate */}
                <div className="p-2 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl border border-indigo-200 flex items-center gap-3 hover:shadow-md transition">
                  <CheckCircleIcon className="h-8 w-8 text-indigo-600" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">
                      Completion Rate
                    </h4>
                    <p className="text-2xl font-bold text-indigo-800">
                      {kpiMetrics.completionRate.toFixed(2)}%
                    </p>
                  </div>
                </div>

                {/* Planned vs Actual */}
                <div className="p-2 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 flex items-center gap-3 hover:shadow-md transition">
                  <DocumentCheckIcon className="h-8 w-8 text-green-600" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">
                      Planned vs Actual
                    </h4>
                    <p className="text-2xl font-bold text-green-800">
                      {kpiMetrics.actualQty} / {kpiMetrics.plannedQty}
                    </p>
                  </div>
                </div>

                {/* Room Utilization */}
                <div className="p-2 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 flex items-center gap-3 hover:shadow-md transition">
                  <ArrowPathIcon className="h-8 w-8 text-blue-600" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">
                      Room Utilization
                    </h4>
                    <p className="text-2xl font-bold text-blue-800">
                      {kpiMetrics.roomUtilization.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h4 className="text-sm font-semibold text-gray-800">
                  Schedule KPI Summary ({selectedRoom})
                </h4>
                {scheduleKpiData.baselineLocked && (
                  <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    Baseline Locked
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-medium text-gray-500">
                    Original KPI Baseline
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {scheduleKpiData.baselineLocked
                      ? scheduleKpiData.originalKpiBaselineQty
                      : "Not locked"}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-medium text-gray-500">
                    Current Scheduled Planned
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {scheduleKpiData.currentScheduledPlannedQty}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-medium text-gray-500">
                    Actual Completed
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {scheduleKpiData.actualCompletedQty}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-medium text-gray-500">
                    KPI Performance
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {scheduleKpiData.performanceRate.toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Work Order Table */}
          {isRoomLoading ? (
            <div className="text-center py-8 text-gray-600">
              Loading work orders for {selectedRoom}...
            </div>
          ) : (
            <WorkOrderTable
              workOrders={roomWorkOrders}
              workOrdersByDate={workOrdersByDate}
              date={formatDateKey(selectedDate)}
              onDrop={(e) =>
                handleDrop(
                  e,
                  selectedRoom,
                  formatDateKey(selectedDate),
                )
              }
              onDragOver={(e) =>
                handleDragOver(
                  e,
                  selectedRoom,
                  formatDateKey(selectedDate),
                )
              }
              onDragStart={handleDragStart}
              canAssign={canAssignWorkOrders}
              canTransferCompleted={canTransferCompleted}
              canPlaceOnHold={canPlaceOnHold}
              canUseContextMenu={canUseContextMenu}
              isDraggingOver={
                isDraggingOver[
                  `${formatDateKey(selectedDate)}_${selectedRoom}`
                ]
              }
              isReadOnly={isReadOnly}
              onHold={handleHold}
              onContextMenuAction={handleContextMenuAction}
              room={selectedRoom}
              onArchive={handleArchiveWorkOrder}
              onRefreshRoom={() =>
                Promise.all([
                  fetchWorkOrdersByRoom(selectedRoom),
                  refreshGlobalKPIs(),
                  fetchSelectedScheduleKPI(),
                ])
              }
              onReorder={reorderWorkOrdersInRoom}
              onRemoveCleaningTask={handleRemoveCleaningTask}
              onRenameCleaningTask={handleRenameCleaningTask}
            />
          )}

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  QC Cleaning Tasks
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Drag a task into the schedule when the team needs it.
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {cleaningTaskError && (
                  <span className="text-xs text-red-600">
                    {cleaningTaskError}
                  </span>
                )}
                {canEditCleaningConfig &&
                  (isEditingCleaningConfig ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCancelCleaningConfigEdit}
                        disabled={isSavingCleaningConfig}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveCleaningConfig}
                        disabled={isSavingCleaningConfig}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md bg-black text-white hover:bg-gray-800 disabled:opacity-60"
                      >
                        {isSavingCleaningConfig ? "Saving..." : "Save"}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleStartCleaningConfigEdit}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md border border-black bg-black text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Edit durations
                    </button>
                  ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {cleaningTasks.map((task) => {
                const isAlreadyScheduled = scheduledCleaningTypes.has(task.id);
                const isCustomTask = task.id === "custom";
                const isDraggable =
                  canAssignWorkOrders &&
                  !isReadOnly &&
                  (isCustomTask || !isAlreadyScheduled);
                const disabledReason = isAlreadyScheduled
                  ? `${task.title} is already scheduled for ${selectedRoom}.`
                  : "";
                const fieldKey = `${task.id}Minutes`;
                return (
                  <div
                    key={task.id}
                    draggable={isDraggable}
                    onDragStart={
                      isDraggable
                        ? (e) => handleCleaningTaskDragStart(e, task)
                        : undefined
                    }
                    title={disabledReason}
                    className={`border rounded-lg p-4 shadow-sm transition ${
                      isDraggable
                        ? "cursor-grab hover:shadow-md bg-gray-50"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    <div className="text-m font-semibold text-gray-900">
                      {task.title}
                    </div>
                    {isCustomTask && (
                      <div className="mt-2">
                        {isEditingCleaningConfig && canEditCleaningConfig ? (
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={customTaskDuration}
                            onChange={(e) =>
                              setCustomTaskDuration(
                                Math.max(1, Number(e.target.value) || 1),
                              )
                            }
                            disabled={!canAssignWorkOrders || isReadOnly}
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                          />
                        ) : (
                          <div className="text-xs text-gray-600 mt-1">
                            {task.durationMinutes} min
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          Rename available after dropping into schedule.
                        </div>
                        {isEditingCleaningConfig && (
                          <div className="text-xs text-gray-500 mt-1">
                            Minutes
                          </div>
                        )}
                      </div>
                    )}
                    {isAlreadyScheduled && !isEditingCleaningConfig && (
                      <div className="text-sm text-amber-700 mt-1">
                        Already scheduled
                      </div>
                    )}
                    {!isCustomTask && isEditingCleaningConfig && canEditCleaningConfig ? (
                      <div className="mt-2">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={cleaningConfigDraft[fieldKey] ?? ""}
                          disabled={isAlreadyScheduled}
                          title={
                            isAlreadyScheduled
                              ? `${task.title} duration is locked because it is already scheduled.`
                              : ""
                          }
                          onChange={(e) =>
                            handleCleaningConfigChange(fieldKey, e.target.value)
                          }
                          className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Minutes
                        </div>
                      </div>
                    ) : !isCustomTask ? (
                      <div className="text-xs text-gray-600 mt-1">
                        {task.durationMinutes} min
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <UnassignedWOTable
            workOrders={allUnassignedWorkOrders}
            onDragStart={(e, wo) => handleDragStart(e, wo, "unassigned")}
            selectedDate={selectedDate}
            isLoading={isLoading}
            error={error}
            categories={categories}
            canAssign={canAssignWorkOrders}
          />

          <AllergenLegend />
        </div>
      </div>

      {isRoomAllergenModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Room Allergen Rules Modal"
          onClick={handleCloseRoomAllergenModal}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  Room Allergen Rules
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Choose which allergens are allowed in each room. Leave empty
                  for no allergens allowed.
                </p>
              </div>
              <button
                onClick={handleCloseRoomAllergenModal}
                className="text-2xl text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {roomAllergenDraft.map((policy) => (
                <div
                  key={policy.room}
                  className="border border-gray-200 rounded-2xl p-6 bg-white"
                >
                  {/* Room Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                    <div className="font-semibold text-lg text-gray-900">
                      {policy.room}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setAllergenModeForRoom(policy.room, "none")
                        }
                        className="px-4 py-2 text-sm rounded-xl bg-red-800 text-white border  hover: bg-[#cf350d]  transition"
                      >
                        No allergens
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setAllergenModeForRoom(policy.room, "all")
                        }
                        className="px-4 py-2 text-sm rounded-xl text-white bg-[#365daf] hover:bg-blue-800 transition"
                      >
                        Allow all
                      </button>
                    </div>
                  </div>

                  {/* Allergen Checkboxes */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    {ALLERGEN_OPTIONS.map((allergen) => (
                      <label
                        key={`${policy.room}-${allergen}`}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition group"
                      >
                        <input
                          type="checkbox"
                          checked={policy.allowedAllergens.includes(allergen)}
                          onChange={() =>
                            toggleRoomAllergen(policy.room, allergen)
                          }
                          className="w-5 h-5 accent-black rounded border-gray-300 cursor-pointer"
                        />
                        <span className="text-gray-800 text-[15px]">
                          {allergen}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t bg-white p-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCloseRoomAllergenModal}
                className="px-6 py-3 border border-gray-300 rounded-2xl text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRoomAllergenPolicies}
                disabled={isSavingRoomAllergenPolicies}
                className="px-8 py-3 bg-black text-white rounded-2xl font-medium hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {isSavingRoomAllergenPolicies ? "Saving..." : "Save Rules"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isRecurringTaskModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Add Recurring Task Modal"
          onClick={handleCloseRecurringTaskModal}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Add Recurring Task
              </h3>
              <button
                onClick={handleCloseRecurringTaskModal}
                className="text-gray-900 hover:text-gray-700 text-xl font-semibold focus:ring-2 focus:ring-gray-500 focus:outline-none"
                aria-label="Close Modal"
              >
                &times;
              </button>
            </div>
            <RecurringTasks
              onClose={handleCloseRecurringTaskModal}
              onSuccess={() => fetchWorkOrdersByRoom(selectedRoom)}
              defaultDate={selectedDate}
            />
          </div>
        </div>
      )}
      <AnimatePresence>
        {showAssignErrorModal && (
          <AssignErrorModal
            isOpen={showAssignErrorModal}
            error={assignError}
            onAssign={retryAssignRoom}
            onCancel={() => {
              setShowAssignErrorModal(false);
              setPendingAssignment(null);
              setAssignError(null);
            }}
            isLoading={isLoading}
            targetRoom={pendingAssignment?.targetRoom}
          />
        )}
        {showOvertimeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            {/* Backdrop blur + better overlay */}
            <div
              className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 
                 border border-red-200 animate-in fade-in zoom-in duration-200"
            >
              {/* Warning Icon */}
              <div className="flex flex-col items-center text-center">
                <div className="text-6xl mb-4">⚠️</div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  This Work Order Will Cause Overtime
                </h3>

                <p className="text-gray-700 leading-relaxed">
                  Assigning this job in <strong>{overtimeDetails?.room}</strong>{" "}
                  will run past the scheduled post-op cleaning time.
                </p>

                <p className="text-sm text-gray-500 mt-4">
                  The team will need to work overtime to complete it.
                </p>
              </div>

              {/* Single action button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setShowOvertimeModal(false)}
                  className="px-8 py-3 bg-blue-600 text-white font-medium rounded-lg 
                     hover:bg-blue-700 active:scale-95 transition-all duration-150
                     shadow-md hover:shadow-lg"
                >
                  Got it, Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
      <style>
        {`
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
    </div>
  );
}
